import { useState, ReactNode, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import posthog from 'posthog-js';

interface Option<K extends string> {
  id: K;
  label: string;
  description?: string;
  metadata?: Record<string, string>;
  disabled?: boolean;
}

interface SelectorProps<K extends string, T extends Option<K>> {
  label?: string;
  options: T[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  disabled?: boolean;
  className?: string;
  multiple?: boolean;
  width?: string;
  hideLabel?: boolean;
  renderOption?: (option: T, isSelected: boolean) => ReactNode;
  'data-ph-capture-attribute'?: string;
  dropdownDirection?: 'up' | 'down' | 'auto';
}

export function Selector<K extends string, T extends Option<K>>({
  label,
  options,
  value,
  onChange,
  disabled = false,
  className = '',
  multiple = false,
  width = 'w-full',
  hideLabel = false,
  renderOption,
  'data-ph-capture-attribute': dataPhCaptureAttribute,
  dropdownDirection = 'auto',
}: SelectorProps<K, T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownAbove, setDropdownAbove] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (!isOpen || !selectorRef.current || dropdownDirection !== 'auto') {
      if (dropdownDirection === 'up') {
        setDropdownAbove(true);
      } else if (dropdownDirection === 'down') {
        setDropdownAbove(false);
      }
      return;
    }

    const rect = selectorRef.current.getBoundingClientRect();
    const dropdownHeight = Math.min(options.length * 60 + 32, 320 + 32); // Estimate dropdown height
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Position dropdown above if there's more space above or if it would be clipped below
    setDropdownAbove(spaceAbove > spaceBelow && spaceAbove > dropdownHeight);
  }, [isOpen, options.length, dropdownDirection]);

  const selectedValues = Array.isArray(value) ? value : [value];

  const getButtonText = () => {
    if (Array.isArray(value)) {
      if (value.length === 0) return `Select ${label?.toLowerCase() ?? 'option'}...`;
      return `${value.length} ${label?.toLowerCase() ?? 'option'}${value.length !== 1 ? 's' : ''} selected`;
    }
    const selectedOption = options.find((opt) => opt.id === value);
    return selectedOption?.label ?? `Select ${label?.toLowerCase() ?? 'option'}...`;
  };

  const defaultRenderOption = (option: T, isSelected: boolean) => (
    <div className="relative group">
      <div
        className={`group relative p-2 mb-1 rounded-md transition-colors ${
          option.disabled
            ? 'opacity-75 cursor-pointer'
            : isSelected
              ? 'hover:bg-black/5 dark:hover:bg-white/10'
              : 'hover:bg-black/5 dark:hover:bg-white/10'
        }`}
      >
        <div className="flex items-center">
          {multiple && (
            <div
              className={`h-4 w-4 rounded-full border ${
                isSelected && !option.disabled
                  ? 'border-black dark:border-white'
                  : 'border-gray-300 dark:border-gray-600'
              } flex items-center justify-center`}
            >
              {isSelected && !option.disabled && (
                <div className="h-2 w-2 rounded-full bg-black dark:bg-white" />
              )}
            </div>
          )}
          <div className="ml-2 flex-1">
            <div className="flex items-center">
              <p
                className={`text-sm font-medium ${option.disabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-900 dark:text-gray-100'}`}
              >
                {option.label}
              </p>
            </div>
            {option.description && (
              <p
                className={`text-xs ${option.disabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-500 dark:text-gray-300'} mt-1`}
              >
                {option.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={selectorRef}>
      {label && !hideLabel && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`${width} px-4 py-2 text-left bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-black/20 dark:focus:ring-white/20 focus:border-black/20 dark:focus:border-white/20 disabled:opacity-50 text-gray-900 dark:text-gray-100`}
        data-ph-capture-attribute={`${dataPhCaptureAttribute}-button`}
      >
        <div className="flex items-center justify-between">
          <span className="block truncate">{getButtonText()}</span>
          <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </div>
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 ${width} ${
            dropdownAbove ? 'bottom-full mb-1' : 'top-full mt-1'
          } bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg`}
        >
          <div className="p-2 max-h-80 overflow-auto">
            {options.map((option) => (
              <div
                key={option.id}
                onClick={() => {
                  if (option.disabled) return;

                  // Track option selection in PostHog
                  if (dataPhCaptureAttribute) {
                    posthog.capture('dropdown_option_selected', {
                      dropdown: dataPhCaptureAttribute,
                      option_id: option.id,
                      option_label: option.label,
                      'data-ph-capture-attribute': `${dataPhCaptureAttribute}-option-${option.id.toLowerCase().replace(/\s+/g, '-')}`,
                    });
                  }

                  if (multiple) {
                    const newValue = selectedValues.includes(option.id)
                      ? selectedValues.filter((id) => id !== option.id)
                      : [...selectedValues, option.id];
                    onChange(newValue);
                  } else {
                    onChange(option.id);
                    setIsOpen(false);
                  }
                }}
              >
                {renderOption
                  ? renderOption(option, selectedValues.includes(option.id))
                  : defaultRenderOption(option, selectedValues.includes(option.id))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
